/**
 * Build Your Own Bundle Custom JS Component
 * Handles collection tab filtering, persistent temp cart state (via localStorage),
 * mobile drawer sheet toggle, and bulk Ajax Add-to-Cart.
 */

if (!customElements.get('build-your-own-bundle')) {
  customElements.define('build-your-own-bundle', class BuildYourOwnBundle extends HTMLElement {
    constructor() {
      super();
      this.items = new Map(); // variantId -> item object
      this.currencySymbol = this.dataset.currencySymbol || '$';
      this.storageKey = 'byob_temp_cart_' + (this.id || 'default');
      this.initElements();
      this.loadFromStorage();
      this.initEvents();
      this.updateUI();
    }

    initElements() {
      this.tabs = this.querySelectorAll('.byob-tab-btn');
      this.cards = this.querySelectorAll('.byob-card');
      this.collectionTitle = this.querySelector('.byob-collection-title');
      
      // Discount & Offer Settings
      this.enableDiscount = this.dataset.enableDiscount === 'true';
      this.bundleTargetQty = parseInt(this.dataset.bundleTargetQty) || 6;
      this.bundleFixedPrice = parseFloat(this.dataset.bundleFixedPrice) || 499;
      this.bundleFixedPriceCents = this.bundleFixedPrice * 100;
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.parentVariantId = (this.dataset.parentVariantId || '').trim();
      this.parentProductHandle = (this.dataset.parentProductHandle || '').trim();
      this.redirectToCheckout = this.dataset.redirectToCheckout === 'true';

      // Desktop Sidebar Elements
      this.itemsCountSub = this.querySelector('.byob-items-subtitle');
      this.itemsListDesktop = this.querySelector('#byob-desktop-items-list');
      this.totalOriginal = this.querySelector('#byob-desktop-total-original');
      this.totalPrice = this.querySelector('#byob-desktop-total-price');
      this.checkoutBtnDesktop = this.querySelector('#byob-desktop-checkout-btn');

      // Mobile Elements
      this.minBundleItems = parseInt(this.dataset.minBundleItems) || 6;
      this.mobileStickyBar = this.querySelector('.byob-mobile-sticky-bar');
      this.mobileBarCount = this.querySelector('#byob-mobile-bar-count');
      this.mobileBarTotal = this.querySelector('#byob-mobile-bar-total');
      this.mobileBarBtn = this.querySelector('#byob-mobile-bar-btn');
      
      this.mobileDrawerOverlay = this.querySelector('.byob-mobile-drawer-overlay');
      this.mobileDrawerCloseBtn = this.querySelector('.byob-drawer-close-btn');
      this.itemsListMobile = this.querySelector('#byob-mobile-items-list');
      this.totalOriginalMobile = this.querySelector('#byob-mobile-total-original');
      this.totalPriceMobile = this.querySelector('#byob-mobile-total-price');
      this.checkoutBtnMobile = this.querySelector('#byob-mobile-checkout-btn');
    }

    loadFromStorage() {
      try {
        const saved = localStorage.getItem(this.storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            this.items = new Map(parsed);
          }
        }
      } catch (e) {
        console.error('Failed to load BYOB bundle from localStorage:', e);
        this.items = new Map();
      }
    }

    saveToStorage() {
      try {
        const arrayData = Array.from(this.items.entries());
        localStorage.setItem(this.storageKey, JSON.stringify(arrayData));
      } catch (e) {
        console.error('Failed to save BYOB bundle to localStorage:', e);
      }
    }

    initEvents() {
      // Collection Tabs Event Listeners
      this.tabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          this.tabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');

          const collectionHandle = tab.dataset.collectionHandle;
          const collectionTitle = tab.dataset.collectionTitle;
          if (this.collectionTitle && collectionTitle) {
            this.collectionTitle.textContent = collectionTitle;
          }

          this.cards.forEach(card => {
            const cardCollections = (card.dataset.collections || '').split(',');
            if (collectionHandle === 'all' || cardCollections.includes(collectionHandle)) {
              card.style.display = 'flex';
            } else {
              card.style.display = 'none';
            }
          });
        });
      });

      // Product Card Action Buttons & Variant Pickers
      this.cards.forEach(card => {
        const addBtn = card.querySelector('.byob-add-btn');
        const minusBtn = card.querySelector('.byob-minus-btn');
        const plusBtn = card.querySelector('.byob-plus-btn');
        const variantSelect = card.querySelector('.byob-variant-select');

        const getSelectedVariant = () => {
          if (variantSelect) {
            const selectedOpt = variantSelect.options[variantSelect.selectedIndex];
            return {
              id: selectedOpt.value,
              price: parseFloat(selectedOpt.dataset.price),
              comparePrice: parseFloat(selectedOpt.dataset.comparePrice || 0),
              title: card.dataset.productTitle + (selectedOpt.text ? ` - ${selectedOpt.text}` : ''),
              image: card.dataset.productImage
            };
          }
          return {
            id: card.dataset.variantId,
            price: parseFloat(card.dataset.price),
            comparePrice: parseFloat(card.dataset.comparePrice || 0),
            title: card.dataset.productTitle,
            image: card.dataset.productImage
          };
        };

        if (addBtn) {
          addBtn.addEventListener('click', () => {
            const variantData = getSelectedVariant();
            this.addItem(variantData);
          });
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => {
            const variantData = getSelectedVariant();
            this.addItem(variantData);
          });
        }

        if (minusBtn) {
          minusBtn.addEventListener('click', () => {
            const variantData = getSelectedVariant();
            this.updateItemQty(variantData.id, -1);
          });
        }

        if (variantSelect) {
          variantSelect.addEventListener('change', () => {
            this.updateCardControls(card);
          });
        }
      });

      // Mobile Drawer Toggle Listeners
      if (this.mobileBarBtn) {
        this.mobileBarBtn.addEventListener('click', () => this.openMobileDrawer());
      }
      if (this.mobileDrawerCloseBtn) {
        this.mobileDrawerCloseBtn.addEventListener('click', () => this.closeMobileDrawer());
      }
      if (this.mobileDrawerOverlay) {
        this.mobileDrawerOverlay.addEventListener('click', (e) => {
          if (e.target === this.mobileDrawerOverlay) {
            this.closeMobileDrawer();
          }
        });
      }

      // Add to Cart Buttons (Desktop & Mobile)
      if (this.checkoutBtnDesktop) {
        this.checkoutBtnDesktop.addEventListener('click', () => this.addToCart(this.checkoutBtnDesktop));
      }
      if (this.checkoutBtnMobile) {
        this.checkoutBtnMobile.addEventListener('click', () => this.addToCart(this.checkoutBtnMobile));
      }
    }

    addItem(variantData) {
      if (!variantData || !variantData.id) return;
      const existing = this.items.get(variantData.id);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.items.set(variantData.id, {
          id: variantData.id,
          title: variantData.title,
          price: variantData.price,
          comparePrice: variantData.comparePrice,
          image: variantData.image,
          quantity: 1
        });
      }
      this.saveToStorage();
      this.updateUI();
    }

    updateItemQty(variantId, delta) {
      const item = this.items.get(variantId);
      if (!item) return;

      item.quantity += delta;
      if (item.quantity <= 0) {
        this.items.delete(variantId);
      }
      this.saveToStorage();
      this.updateUI();
    }

    removeItem(variantId) {
      this.items.delete(variantId);
      this.saveToStorage();
      this.updateUI();
    }

    clearBundle() {
      this.items.clear();
      try {
        localStorage.removeItem(this.storageKey);
      } catch (e) {}
      this.updateUI();
      this.closeMobileDrawer();
    }

    openMobileDrawer() {
      if (this.mobileDrawerOverlay) {
        this.mobileDrawerOverlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    }

    closeMobileDrawer() {
      if (this.mobileDrawerOverlay) {
        this.mobileDrawerOverlay.classList.remove('active');
        document.body.style.overflow = '';
      }
    }

    getTotalQuantity() {
      let total = 0;
      this.items.forEach(item => total += item.quantity);
      return total;
    }

    getRawSubtotal() {
      let total = 0;
      this.items.forEach(item => total += (item.price * item.quantity));
      return total;
    }

    getCalculation() {
      const totalQty = this.getTotalQuantity();
      const rawSubtotal = this.getRawSubtotal();

      let finalTotal = rawSubtotal;
      let isDiscountApplied = false;

      if (this.enableDiscount && this.bundleTargetQty > 0 && this.bundleFixedPriceCents > 0) {
        if (totalQty >= this.bundleTargetQty) {
          const fullBundles = Math.floor(totalQty / this.bundleTargetQty);
          const remainderQty = totalQty % this.bundleTargetQty;

          const bundleCost = fullBundles * this.bundleFixedPriceCents;
          
          if (remainderQty === 0) {
            finalTotal = bundleCost;
          } else {
            const avgItemPrice = totalQty > 0 ? (rawSubtotal / totalQty) : 0;
            finalTotal = bundleCost + (remainderQty * avgItemPrice);
          }

          if (finalTotal < rawSubtotal) {
            isDiscountApplied = true;
          }
        }
      }

      return {
        totalQty,
        rawSubtotal,
        finalTotal,
        isDiscountApplied
      };
    }

    formatMoney(centsOrUnits) {
      const amount = (centsOrUnits / 100).toFixed(2);
      return `${this.currencySymbol}${amount}`;
    }

    updateUI() {
      const calc = this.getCalculation();
      const totalQty = calc.totalQty;
      const minQty = this.minBundleItems || 1;

      // Update Card Action Buttons
      this.cards.forEach(card => this.updateCardControls(card));

      // Update Subtitles
      if (this.itemsCountSub) {
        this.itemsCountSub.textContent = `${totalQty} item(s)`;
      }

      if (this.mobileBarCount) {
        if (minQty > 1) {
          this.mobileBarCount.textContent = `${totalQty} OUT OF ${minQty} ITEMS SELECTED`;
        } else {
          this.mobileBarCount.textContent = `${totalQty} ITEM${totalQty === 1 ? '' : 'S'} SELECTED`;
        }
      }

      // Update Price Displays
      const formattedFinal = this.formatMoney(calc.finalTotal);
      const formattedOriginal = this.formatMoney(calc.rawSubtotal);

      if (this.totalPrice) this.totalPrice.textContent = formattedFinal;
      if (this.totalPriceMobile) this.totalPriceMobile.textContent = formattedFinal;
      if (this.mobileBarTotal) this.mobileBarTotal.textContent = formattedFinal;

      if (calc.isDiscountApplied && calc.rawSubtotal > calc.finalTotal) {
        if (this.totalOriginal) {
          this.totalOriginal.textContent = formattedOriginal;
          this.totalOriginal.style.display = 'inline';
        }
        if (this.totalOriginalMobile) {
          this.totalOriginalMobile.textContent = formattedOriginal;
          this.totalOriginalMobile.style.display = 'inline';
        }
      } else {
        if (this.totalOriginal) this.totalOriginal.style.display = 'none';
        if (this.totalOriginalMobile) this.totalOriginalMobile.style.display = 'none';
      }

      // Render Selected Items Lists
      this.renderItemsList(this.itemsListDesktop);
      this.renderItemsList(this.itemsListMobile);

      // Toggle Checkout Button States & Dynamic Helper Text
      const isMinMet = totalQty >= minQty;
      const itemsNeeded = Math.max(0, minQty - totalQty);

      const baseBtnText = this.dataset.checkoutBtnText || 'ADD TO CART';
      let btnText = baseBtnText;

      if (!isMinMet && totalQty > 0) {
        btnText = `ADD ${itemsNeeded} MORE TO CHECKOUT`;
      } else if (!isMinMet && totalQty === 0) {
        btnText = `ADD ${minQty} ITEM${minQty > 1 ? 'S' : ''} TO START`;
      }

      if (this.checkoutBtnDesktop) {
        const textSpan = this.checkoutBtnDesktop.querySelector('span:not(.byob-spinner)');
        if (textSpan) textSpan.textContent = btnText;
        this.checkoutBtnDesktop.disabled = !isMinMet;
      }

      if (this.checkoutBtnMobile) {
        const textSpan = this.checkoutBtnMobile.querySelector('span:not(.byob-spinner)');
        if (textSpan) textSpan.textContent = btnText;
        this.checkoutBtnMobile.disabled = !isMinMet;
      }
    }

    updateCardControls(card) {
      const variantSelect = card.querySelector('.byob-variant-select');
      let currentVariantId = card.dataset.variantId;
      if (variantSelect) {
        currentVariantId = variantSelect.value;
      }

      const item = this.items.get(currentVariantId);
      const addBtn = card.querySelector('.byob-add-btn');
      const qtyStepper = card.querySelector('.byob-qty-stepper');
      const stepperVal = card.querySelector('.byob-stepper-val');

      if (item && item.quantity > 0) {
        card.classList.add('is-selected');
        if (addBtn) addBtn.style.display = 'none';
        if (qtyStepper) qtyStepper.style.display = 'flex';
        if (stepperVal) stepperVal.textContent = item.quantity;
      } else {
        card.classList.remove('is-selected');
        if (addBtn) addBtn.style.display = 'flex';
        if (qtyStepper) qtyStepper.style.display = 'none';
      }
    }

    renderItemsList(container, discountPercent) {
      if (!container) return;

      if (this.items.size === 0) {
        container.innerHTML = `<div style="text-align: center; color: #888; padding: 20px; font-size: 13px;">Your bundle is empty. Add products to get started!</div>`;
        return;
      }

      let html = '';
      this.items.forEach(item => {
        const itemLineTotal = item.price * item.quantity;
        const formattedPrice = this.formatMoney(itemLineTotal);

        html += `
          <div class="byob-item-row" data-variant-id="${item.id}">
            <img class="byob-item-thumb" src="${item.image}" alt="${item.title}" loading="lazy">
            <div class="byob-item-details">
              <div class="byob-item-name" title="${item.title}">${item.title}</div>
              <div class="byob-item-pricing">
                <span>${formattedPrice}</span>
              </div>
            </div>
            <div class="byob-item-controls">
              <div class="byob-qty-stepper" style="padding: 2px 6px;">
                <button type="button" class="byob-stepper-btn byob-row-minus" style="width: 24px; height: 24px; font-size: 14px;">-</button>
                <span class="byob-stepper-val" style="min-width: 18px; font-size: 13px;">${item.quantity}</span>
                <button type="button" class="byob-stepper-btn byob-row-plus" style="width: 24px; height: 24px; font-size: 14px;">+</button>
              </div>
              <button type="button" class="byob-remove-btn" title="Remove item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
              </button>
            </div>
          </div>
        `;
      });

      container.innerHTML = html;

      // Attach row event handlers
      container.querySelectorAll('.byob-item-row').forEach(row => {
        const variantId = row.dataset.variantId;
        const rowMinus = row.querySelector('.byob-row-minus');
        const rowPlus = row.querySelector('.byob-row-plus');
        const removeBtn = row.querySelector('.byob-remove-btn');

        if (rowMinus) rowMinus.addEventListener('click', () => this.updateItemQty(variantId, -1));
        if (rowPlus) rowPlus.addEventListener('click', () => this.updateItemQty(variantId, 1));
        if (removeBtn) removeBtn.addEventListener('click', () => this.removeItem(variantId));
      });
    }

    async addToCart(targetBtn) {
      if (this.items.size === 0) return;

      // Dynamically resolve variant ID if missing using the product handle .js endpoint
      if (!this.parentVariantId && this.parentProductHandle) {
        try {
          const pRes = await fetch(`/products/${encodeURIComponent(this.parentProductHandle)}.js`);
          const pData = await pRes.json();
          if (pData && pData.variants && pData.variants.length > 0) {
            this.parentVariantId = pData.variants[0].id;
          }
        } catch (e) {
          console.error('Failed to dynamically resolve parent variant ID:', e);
        }
      }

      const itemsToAdd = [];
      const bundleSize = this.bundleTargetQty || 6;

      if (this.parentVariantId) {
        // Flatten all selected items into individual unit objects
        const flatItems = [];
        this.items.forEach(item => {
          for (let i = 0; i < item.quantity; i++) {
            flatItems.push({
              id: item.id,
              title: item.title,
              price: item.price
            });
          }
        });

        const totalQty = flatItems.length;
        const fullBundleCount = Math.floor(totalQty / bundleSize);
        const remainderQty = totalQty % bundleSize;

        // 1. Create Full 6-Pack Bundles (e.g. 6 items -> 1 bundle, 12 items -> 2 bundles, 18 items -> 3 bundles)
        for (let b = 0; b < fullBundleCount; b++) {
          const startIndex = b * bundleSize;
          const bundleSlice = flatItems.slice(startIndex, startIndex + bundleSize);

          const itemSummaryMap = new Map();
          bundleSlice.forEach(unit => {
            const currentCount = itemSummaryMap.get(unit.title) || 0;
            itemSummaryMap.set(unit.title, currentCount + 1);
          });

          const summaryLines = [];
          itemSummaryMap.forEach((qty, title) => {
            summaryLines.push(`${qty}x ${title}`);
          });

          itemsToAdd.push({
            id: this.parentVariantId,
            quantity: 1,
            properties: {
              'Included Items': summaryLines.join(', ')
            }
          });
        }

        // 2. Add Remaining Items (e.g. 7th, 8th, 13th items) as separate individual products
        if (remainderQty > 0) {
          const remainderSlice = flatItems.slice(fullBundleCount * bundleSize);
          const remainderSummaryMap = new Map();

          remainderSlice.forEach(unit => {
            const current = remainderSummaryMap.get(unit.id) || 0;
            remainderSummaryMap.set(unit.id, current + 1);
          });

          remainderSummaryMap.forEach((qty, variantId) => {
            itemsToAdd.push({
              id: variantId,
              quantity: qty
            });
          });
        }
      } else {
        // Fallback: Add individual items if parentVariantId is not configured
        this.items.forEach(item => {
          itemsToAdd.push({
            id: item.id,
            quantity: item.quantity
          });
        });
      }

      if (targetBtn) targetBtn.classList.add('loading');

      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(s => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const payload = {
        items: itemsToAdd,
        sections: sectionsToRender,
        sections_url: window.location.pathname
      };

      fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      .then(res => res.json())
      .then(async data => {
        if (data.status) {
          alert(data.description || 'Failed to add items to cart.');
        } else {
          // If a specific discount code is configured, auto-apply it to the session
          if (this.discountCode) {
            try {
              await fetch(`/discount/${encodeURIComponent(this.discountCode)}`);
            } catch (e) {
              console.error('Failed to auto-apply discount code:', e);
            }
          }

          // If direct checkout redirect option is enabled in theme customizer
          if (this.redirectToCheckout) {
            window.location.href = window.routes?.checkout_url || '/checkout';
            this.clearBundle();
            return;
          }

          // Re-fetch updated cart sections so Automatic Discounts or Discount Codes render instantly in cart drawer
          try {
            const sectionsRes = await fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`);
            const updatedSections = await sectionsRes.json();
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(updatedSections);
            } else {
              window.location.href = window.routes?.cart_url || '/cart';
            }
          } catch (e) {
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(data);
            } else {
              window.location.href = window.routes?.cart_url || '/cart';
            }
          }

          this.clearBundle();
        }
      })
      .catch(err => {
        console.error('Ajax Add to Cart error:', err);
      })
      .finally(() => {
        if (targetBtn) targetBtn.classList.remove('loading');
      });
    }
  });
}
